import {
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

/**
 * Severity levels for notifications.
 * NOTE: Severity is not currently surfaced in the UI — notifications are
 * displayed and filtered without regard to this field.
 */
export type NotificationSeverity = 'low' | 'normal' | 'high' | 'critical';

export type NotificationPayload = {
  title: string;
  description?: string;
  link?: string;
  severity?: NotificationSeverity;
  topic?: string;
  /** Legacy field — some notification sources use `info` instead of `description` */
  info?: string;
  /** Legacy field — some notification sources use `message` instead of `description` */
  message?: string;
};

export type Notification = {
  id: string;
  read?: boolean;
  saved?: boolean;
  created?: string;
  origin?: string;
  payload: NotificationPayload;
};

export type GetNotificationsOptions = {
  limit?: number;
  offset?: number;
  read?: boolean;
  saved?: boolean;
  search?: string;
  topic?: string;
  minimumSeverity?: NotificationSeverity;
};

export type GetNotificationsResponse = {
  totalCount: number;
  notifications: Notification[];
};

export type NotificationsStatus = {
  unread: number;
};

export type UpdateNotificationsOptions = {
  ids: string[];
  read?: boolean;
  saved?: boolean;
};

export type CreateNotificationOptions = {
  recipients: { type: 'broadcast' } | { type: 'entity'; entityRefs: string[] };
  payload: {
    title: string;
    description?: string;
    link?: string;
    severity?: NotificationSeverity;
    topic?: string;
    origin?: string;
  };
};

export interface NotificationApi {
  getStatus(): Promise<NotificationsStatus>;
  getNotifications(
    options?: GetNotificationsOptions,
  ): Promise<GetNotificationsResponse>;
  getNotification(id: string): Promise<Notification>;
  updateNotifications(options: UpdateNotificationsOptions): Promise<unknown>;
  createNotification(options: CreateNotificationOptions): Promise<void>;
}

export const notificationApiRef = createApiRef<NotificationApi>({
  id: 'mercedes.notification.service',
});

export class NotificationClient implements NotificationApi {
  constructor(
    private readonly deps: {
      discoveryApi: typeof discoveryApiRef.T;
      fetchApi: typeof fetchApiRef.T;
    },
  ) {}

  private async baseUrl(): Promise<string> {
    return this.deps.discoveryApi.getBaseUrl('notifications');
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const base = await this.baseUrl();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15_000);
    try {
      const response = await this.deps.fetchApi.fetch(`${base}${path}`, {
        ...init,
        signal: controller.signal,
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(
          `Notifications request failed (${response.status}): ${text || path}`,
        );
      }
      return (await response.json()) as T;
    } finally {
      clearTimeout(timeout);
    }
  }

  async getStatus(): Promise<NotificationsStatus> {
    return this.request<NotificationsStatus>('/status');
  }

  async getNotifications(
    options?: GetNotificationsOptions,
  ): Promise<GetNotificationsResponse> {
    const query = new URLSearchParams();
    if (options?.limit !== undefined) query.set('limit', String(options.limit));
    if (options?.offset !== undefined)
      query.set('offset', String(options.offset));
    if (options?.read !== undefined)
      query.set('read', options.read ? 'true' : 'false');
    if (options?.saved !== undefined)
      query.set('saved', options.saved ? 'true' : 'false');
    if (options?.search) query.set('search', options.search);
    if (options?.topic) query.set('topic', options.topic);
    if (options?.minimumSeverity)
      query.set('minimumSeverity', options.minimumSeverity);

    const suffix = query.toString();
    const path = suffix ? `/notifications?${suffix}` : '/notifications';
    return this.request<GetNotificationsResponse>(path);
  }

  async getNotification(id: string): Promise<Notification> {
    return this.request<Notification>(
      `/notifications/${encodeURIComponent(id)}`,
    );
  }

  async updateNotifications(
    options: UpdateNotificationsOptions,
  ): Promise<unknown> {
    return this.request('/notifications/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    });
  }

  async createNotification(options: CreateNotificationOptions): Promise<void> {
    await this.request<void>('/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    });
  }
}

export const notificationApiFactory = createApiFactory({
  api: notificationApiRef,
  deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
  factory: ({ discoveryApi, fetchApi }) =>
    new NotificationClient({ discoveryApi, fetchApi }),
});
